import { useHandler } from "@redotech/react-util/hook";
import LayoutRightIcon from "@redotech/redo-icons/layout-right.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useLocalStorage } from "usehooks-ts";

export const FULFILLMENT_PANEL_ONBOARDING_MAX_WIDTH = 1023;

const DISMISSAL_KEY_PREFIX =
  "redo.fulfillment.scan.fulfillment-panel-onboarding-dismissed";

export function getFulfillmentPanelOnboardingDismissalKey(userId: string) {
  return `${DISMISSAL_KEY_PREFIX}.${userId}`;
}

export interface FulfillmentPanelOnboardingButtonProps {
  fulfillmentPanelOpen: boolean;
  onOpen: () => void;
  panelId?: string;
  showOnboarding: boolean;
  userId: string;
}

export const FulfillmentPanelOnboardingButton = memo(
  function FulfillmentPanelOnboardingButton({
    fulfillmentPanelOpen,
    onOpen,
    panelId = "scan-fulfillment-panel",
    showOnboarding,
    userId,
  }: FulfillmentPanelOnboardingButtonProps) {
    const storageKey = getFulfillmentPanelOnboardingDismissalKey(userId);
    const [dismissed, setDismissed] = useLocalStorage(storageKey, false);

    const dismiss = useHandler(() => {
      setDismissed(true);
    });
    const handleOpen = useHandler(() => {
      dismiss();
      onOpen();
    });

    return (
      <Tooltip
        arrow
        darkMode
        describeChild
        disableFocusListener
        disableHoverListener
        disableTouchListener
        leftAlign
        open={showOnboarding && !dismissed}
        placement="bottom-end"
        title={
          <Flex align="flex-start" gap="sm">
            <Text fontSize="xs">
              Fulfillment actions and details can be accessed by clicking the
              sidebar button.
            </Text>
            <RedoButton
              aria-label="Dismiss fulfillment details tip"
              hierarchy="tertiary"
              IconLeading={XCloseIcon}
              onClick={dismiss}
              size="xs"
            />
          </Flex>
        }
      >
        <RedoButton
          aria-controls={panelId}
          aria-expanded={fulfillmentPanelOpen}
          aria-label="Open fulfillment details"
          data-testid="open-fulfillment-panel"
          hierarchy="secondary"
          IconLeading={LayoutRightIcon}
          onClick={handleOpen}
          size="sm"
        />
      </Tooltip>
    );
  },
);
